import React, { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  PanelHeader,
  Button,
  Divider,
  Tabs,
  Drawer,
} from "@pushpress/shared-components";
import EventDetails from "./EventDetails";
import EventSettings from "./EventSettings";
import {
  EventForm,
  parseEventFormDefaultValues,
  parseEventFormToMutationInputValue,
} from "./../CalendarForm.utils";
import { useToggle } from "../../../../hooks/state";
import { defaultToastOptions, extractError } from "../../../../utils/misc";
import { toast } from "react-toastify";
import {
  useGetCalendarItemForEditQuery,
  useUpdateCalendarItemMutation,
} from "../CalendarForm-generated.hooks";
import { AppContext } from "../../../../context/app";

export interface OnSuccessParams {
  uuid: string;
  toInviteOnly: boolean;
}

type EditEventProps = {
  visible: boolean;
  calendarItemUuid?: string;
  onClose: VoidFunction;
  onSuccess: ({ uuid }: OnSuccessParams) => void;
};

const EditEventDrawer = (props: EditEventProps) => {
  return (
    <Drawer visible={props.visible} anchor="right" width="400px">
      {props.visible && <EditEventHydrator {...props} />}
    </Drawer>
  );
};

const EditEventHydrator = (props: EditEventProps) => {
  const { calendarItemUuid } = props;
  const { client } = useContext(AppContext);
  const { data } = useGetCalendarItemForEditQuery({
    variables: {
      getCalendarItemInput: {
        uuid: calendarItemUuid || "",
        clientUuid: client.client_uuid || client.uuid,
      },
    },
    skip: !calendarItemUuid,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const defaultValues = useMemo<Partial<EventForm> | undefined>(() => {
    if (!data?.getCalendarItem) return undefined;

    return parseEventFormDefaultValues(data.getCalendarItem);
  }, [data?.getCalendarItem]);

  return (
    <>
      {defaultValues && <EditEvent {...props} defaultValues={defaultValues} />}
    </>
  );
};

const EditEvent = ({
  calendarItemUuid,
  onSuccess,
  onClose,
  defaultValues,
}: EditEventProps & { defaultValues: Partial<EventForm> }) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    trigger,
  } = useForm<EventForm>({
    defaultValues,
    mode: "onTouched",
  });
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [loadingSubmit, loadingSubmitActions] = useToggle();
  const [updateCalendarItemMutation] = useUpdateCalendarItemMutation();

  const onSubmit = async (values: EventForm) => {
    if (!calendarItemUuid) return;

    try {
      loadingSubmitActions.on();
      const parsedValues = parseEventFormToMutationInputValue(
        calendarItemUuid,
        values,
      );
      await updateCalendarItemMutation({
        variables: {
          updateCalendarItemInput: parsedValues,
        },
      });
      toast.success(
        `Success! Your event has been updated!`,
        defaultToastOptions,
      );

      onSuccess({
        uuid: calendarItemUuid,
        toInviteOnly:
          defaultValues.inviteOnly !== values.inviteOnly
            ? values.inviteOnly
            : undefined,
      });
    } catch (err) {
      toast.error(extractError(err), defaultToastOptions);
    } finally {
      loadingSubmitActions.off();
    }
  };

  return (
    <>
      <PanelHeader title="Edit event">
        <PanelHeader.ActionMenu>
          <Button
            text="Cancel"
            buttonType="secondary"
            size="medium"
            onClick={onClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            size="medium"
            disabled={!isValid}
            iconLocation="left"
            onClick={handleSubmit(onSubmit)}
            loading={loadingSubmit}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <Divider />
      <Tabs
        tabHeaders={["Details", "Settings"]}
        selectedIndex={selectedTabIndex}
        changeTab={setSelectedTabIndex}
        onTabClick={setSelectedTabIndex}
        align="left"
        hideWithCss
        onTabFocus={setSelectedTabIndex}
      >
        <EventDetails control={control} editMode trigger={trigger} />
        <EventSettings control={control} />
      </Tabs>
    </>
  );
};

export default EditEventDrawer;
